import { zodResolver } from "@hookform/resolvers/zod";
import { Turnstile } from "@marsidev/react-turnstile";
import { Link, useSearch } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { CookieSettingsButton } from "@/components/CookieSettingsButton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { markPendingGoogleSignUp, trackSignUp } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { authClient } from "../../lib/auth";
import LoadingSpinner from "../loading-spinner";
import { Field, FieldError, FieldGroup, FieldLabel } from "../ui/field";
import GoogleIcon from "./GoogleIcon";
import { googleErrorMessage } from "./googleErrorMessage";

// Zod validation schema
const signupSchema = z
    .object({
        name: z.string().min(1, "Name is required").min(2, "Name must be at least 2 characters long"),
        email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
        password: z
            .string()
            .min(1, "Password is required")
            .min(8, "Password must be at least 8 characters long")
            .regex(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                "Password must contain at least one uppercase letter, one lowercase letter, and one number"
            ),
        confirmPassword: z.string().min(1, "Please confirm your password"),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ["confirmPassword"],
    });

type SignupFormData = z.infer<typeof signupSchema>;

export function SignupForm({ className, ...props }: React.ComponentProps<"div">) {
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
    const [signupError, setSignupError] = useState<string | null>(null);
    const { redirect, email, error } = useSearch({ strict: false }) as {
        redirect: string | undefined;
        email: string | undefined;
        error: string | undefined;
    };
    const [token, setToken] = useState("");

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(signupSchema),
        mode: "onBlur",
        defaultValues: {
            email: email,
        },
    });

    const onSubmit = async (data: SignupFormData) => {
        setIsLoading(true);
        setSignupError(null);

        const { error, data: authData } = await authClient.signUp.email({
            email: data.email,
            password: data.password,
            name: data.name,
            //callbackURL: redirect || window.location.origin,
            fetchOptions: {
                headers: {
                    "x-captcha-response": token,
                },
            },
        });
        if (error) {
            console.error("Signup error:", error);
            setSignupError(error.message || "Signup failed. Please try again.");
            setIsLoading(false);
            if (window.turnstile) {
                window.turnstile.reset(); // Resets all widgets on the page
            }
            return;
        }

        trackSignUp({ method: "email", email: data.email, userId: authData?.user?.id });
        // for some reason the builtin callback isn't  working so we redirect manually
        window.location.href = redirect || window.location.origin;
    };

    const handleGoogleSignup = async () => {
        setIsLoadingGoogle(true);
        try {
            markPendingGoogleSignUp();
            await authClient.signIn.social({
                provider: "google",
                callbackURL: redirect || window.location.origin,
                errorCallbackURL: `${window.location.origin}/signup`,
            });
        } catch (error) {
            setIsLoadingGoogle(false);
            console.error("Google signup error:", error);
            throw error;
        }
    };

    const id = useId();

    return (
        <div className={cn("flex flex-col gap-6", className)} {...props}>
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">
                        <img src="/orsane.svg" alt="Orsane" className="w-auto h-4 mx-auto mb-2" />
                    </CardTitle>
                    <CardDescription className="text-neutral-600">
                        Start collaborating with your team in seconds
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} id={id}>
                        <div className="grid gap-6">
                            <div className="flex flex-col gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full text-base"
                                    onClick={handleGoogleSignup}
                                    disabled={isLoadingGoogle || isLoading}
                                >
                                    {isLoadingGoogle && <LoadingSpinner />}
                                    {!isLoadingGoogle && <GoogleIcon />}
                                    Sign up with Google
                                </Button>
                                <FieldError className="-mt-1">{googleErrorMessage(error)}</FieldError>
                            </div>
                            <div className="after:border-border relative text-center  after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                                <span className="bg-card text-muted-foreground relative z-10 px-2">
                                    Or continue with
                                </span>
                            </div>

                            {signupError && (
                                <div className=" text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                                    {signupError}
                                </div>
                            )}

                            <div className="grid gap-4">
                                <FieldGroup className="gap-4">
                                    <Field>
                                        <FieldLabel htmlFor="name">Full Name</FieldLabel>
                                        <Input
                                            type="text"
                                            placeholder="John Doe"
                                            {...register("name")}
                                            className={errors.name ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.email]} />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="email">Email</FieldLabel>
                                        <Input
                                            type="email"
                                            placeholder="m@example.com"
                                            {...register("email")}
                                            className={errors.email ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.email]} />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="password">Password</FieldLabel>
                                        <Input
                                            type="password"
                                            {...register("password")}
                                            className={errors.password ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.password]} />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="confirmPassword">Confirm Password</FieldLabel>
                                        <Input
                                            type="password"
                                            {...register("confirmPassword")}
                                            className={errors.confirmPassword ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.confirmPassword]} />
                                    </Field>
                                </FieldGroup>
                                <Turnstile
                                    onSuccess={setToken}
                                    className="w-full mx-auto"
                                    options={{
                                        theme: "light",
                                        size: "flexible",
                                    }}
                                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY!}
                                />
                                <Button
                                    type="submit"
                                    form={id}
                                    className="w-full text-base"
                                    disabled={isLoading || !token}
                                >
                                    {isLoading ? "Creating account..." : "Create account"}
                                </Button>
                            </div>
                            <div className="text-center text-neutral-600">
                                Already have an account?{" "}
                                <Link
                                    to="/login"
                                    className="underline underline-offset-4"
                                    search={{
                                        redirect: redirect,
                                        email: email,
                                    }}
                                >
                                    Sign in
                                </Link>
                            </div>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-sm text-balance *:[a]:underline *:[a]:underline-offset-4">
                By signing up, you agree to our <br />
                <a href="https://www.orsane.app/terms" target="_blank" rel="noopener">
                    Terms of Service
                </a>{" "}
                and{" "}
                <a href="https://www.orsane.app/privacy" target="_blank" rel="noopener">
                    Privacy Policy
                </a>
                .
                <br />
                <CookieSettingsButton>Cookie settings</CookieSettingsButton>
            </div>
        </div>
    );
}
